/* global JQuery */
import { Lang } from '../Lang';
import { Widget2 } from 'bambooo';
import crossfilter from 'crossfilter2';
import * as d3 from 'd3';
import * as dc from 'dc';
const DAY_MS = 24 * 60 * 60 * 1000;
const escapeHtml = (s) => s
    .replace(/&/gu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;')
    .replace(/"/gu, '&quot;')
    .replace(/'/gu, '&#39;');
/**
 * Resolve dimensions on the SightingsEntry. `parseDay` truncates to the day in
 * UTC ms; `parseHour` pulls the hour out of the `HH:mm:ss` tour-start string.
 */
const parseDay = (entry) => {
    var _a;
    const datePart = ((_a = entry.date) !== null && _a !== void 0 ? _a : '').split(' ')[0];
    const t = Date.parse(datePart);
    if (Number.isNaN(t)) {
        return 0;
    }
    return t - (t % DAY_MS);
};
const parseHour = (entry) => {
    var _a;
    const ts = (_a = entry.tour_start) !== null && _a !== void 0 ? _a : '';
    const m = (/^(\d{1,2}):/u).exec(ts);
    if (!m) {
        return -1;
    }
    return parseInt(m[1], 10);
};
/**
 * Crossfilter / dc.js dashboard sitting above the SightingMap. Charts share a
 * single crossfilter instance over the currently loaded sightings; selecting on
 * any chart fires `onFiltered` with the surviving entries so the map can be
 * redrawn against the same subset.
 */
export class SightingDashboard extends Widget2 {
    constructor(parent) {
        super({
            element: jQuery('<div class="sighting-dashboard mb-2"/>')
        });
        this._lookups = {
            species: new Map(),
            vehicles: new Map()
        };
        this._onFiltered = null;
        this._cf = null;
        /**
         * Live chart instances; kept for filterAll() on reset and deregister on clear.
         * dc.js has no bundled types so this is `any[]`.
         */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this._charts = [];
        const root = this._element;
        root.appendTo(SightingDashboard._asParent(parent));
        const header = jQuery('<div class="sighting-dashboard-header"/>').appendTo(root);
        this._statusEl = jQuery('<span class="sighting-dashboard-status"/>').appendTo(header);
        this._resetBtn = jQuery('<button type="button" class="btn btn-default btn-xs">Reset filters</button>').appendTo(header);
        this._resetBtn.prop('disabled', true);
        this._resetBtn.on('click', () => {
            this.resetFilters();
        });
        this._timeChartEl = jQuery('<div class="sighting-dashboard-chart sighting-dashboard-time"/>').appendTo(root);
        const row = jQuery('<div class="sighting-dashboard-row"/>').appendTo(root);
        this._speciesChartEl = jQuery('<div class="sighting-dashboard-chart sighting-dashboard-species"/>').appendTo(row);
        this._hourChartEl = jQuery('<div class="sighting-dashboard-chart sighting-dashboard-hour"/>').appendTo(row);
        this._beaufortChartEl = jQuery('<div class="sighting-dashboard-chart sighting-dashboard-beaufort"/>').appendTo(row);
    }
    setLookups(lookups) {
        this._lookups = lookups;
    }
    setOnFiltered(cb) {
        this._onFiltered = cb;
    }
    /**
     * Replace the entire dataset and (re)build the charts. Called whenever the
     * server-side filter changes or after the bulk "load all" backfill.
     */
    setData(sightings) {
        this.clear();
        if (sightings.length === 0) {
            this._statusEl.text(Lang.i().l('No sightings under current filter.'));
            this._resetBtn.prop('disabled', true);
            return;
        }
        this._statusEl.text(`${sightings.length} sightings — drag on the time bar or click categories to filter the map.`);
        this._resetBtn.prop('disabled', false);
        const cf = crossfilter(sightings);
        this._cf = cf;
        // Time chart -----------------------------------------------------------------------------
        const days = sightings.map((d) => parseDay(d)).filter((t) => t > 0);
        const minDay = days.length > 0 ? Math.min(...days) : Date.now();
        const maxDay = days.length > 0 ? Math.max(...days) : Date.now();
        /*
         * Pick the time-bucket granularity from the data span so the bar chart
         * never draws sub-pixel (negative-width) bars. With the manually-entered
         * old data reaching back to the 1990s a daily bucket would be ~11k bars
         * across ~750px — each bar narrower than the inter-bar gap, which makes
         * the SVG `<rect>` width go negative ("A negative value is not valid").
         * Day up to ~1y span, week up to ~5y, month beyond.
         */
        const spanDays = (maxDay - minDay) / DAY_MS;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let xUnits = d3.timeDay;
        let bucketOf = (t) => t;
        let padMs = DAY_MS;
        if (spanDays > 1500) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            xUnits = d3.timeMonth;
            bucketOf = (t) => {
                const d = new Date(t);
                return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
            };
            padMs = 31 * DAY_MS;
        }
        else if (spanDays > 370) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            xUnits = d3.timeWeek;
            bucketOf = (t) => t - (t % (7 * DAY_MS));
            padMs = 7 * DAY_MS;
        }
        const dayDim = cf.dimension((d) => bucketOf(parseDay(d)));
        const dayGroup = dayDim.group();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const timeChart = dc.barChart(this._timeChartEl[0]);
        timeChart
            .width(this._approxWidth(this._timeChartEl, 800))
            .height(140)
            .margins({ top: 10, right: 25, bottom: 28, left: 40 })
            .dimension(dayDim)
            .group(dayGroup)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .x(d3.scaleTime().domain([new Date(minDay - padMs), new Date(maxDay + padMs)]))
            .xUnits(xUnits)
            .elasticY(true)
            .brushOn(true)
            .renderHorizontalGridLines(true)
            .yAxisLabel('Sightings')
            .title((d) => {
            const date = new Date(d.key);
            return `${date.toISOString().slice(0, 10)}: ${d.value}`;
        });
        timeChart.yAxis().ticks(4);
        this._charts.push(timeChart);
        // Species chart (top-N row chart) --------------------------------------------------------
        const speciesDim = cf.dimension((d) => { var _a; return (_a = d.species_id) !== null && _a !== void 0 ? _a : 0; });
        const speciesGroup = speciesDim.group();
        const speciesLookup = this._lookups.species;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const speciesChart = dc.rowChart(this._speciesChartEl[0]);
        speciesChart
            .width(this._approxWidth(this._speciesChartEl, 320))
            .height(220)
            .margins({ top: 10, right: 10, bottom: 28, left: 10 })
            .dimension(speciesDim)
            .group(speciesGroup)
            .elasticX(true)
            .cap(10)
            .othersGrouper(null)
            .label((d) => {
            if (d.key === 0) {
                return `(no species) — ${d.value}`;
            }
            const s = speciesLookup.get(d.key);
            const name = s ? s.name.split(',')[0] : `#${d.key}`;
            return `${name} — ${d.value}`;
        })
            .title((d) => `${d.value} sightings`);
        speciesChart.xAxis().ticks(4);
        this._charts.push(speciesChart);
        // Hour-of-day chart ----------------------------------------------------------------------
        const hourDim = cf.dimension((d) => parseHour(d));
        const hourGroup = hourDim.group();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const hourChart = dc.barChart(this._hourChartEl[0]);
        hourChart
            .width(this._approxWidth(this._hourChartEl, 320))
            .height(220)
            .margins({ top: 10, right: 15, bottom: 28, left: 35 })
            .dimension(hourDim)
            .group(hourGroup)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .x(d3.scaleLinear().domain([-1, 24]))
            .xUnits(() => 25)
            .elasticY(true)
            .brushOn(true)
            .yAxisLabel('Sightings')
            .xAxisLabel('Hour of day')
            .title((d) => {
            if (d.key === -1) {
                return `(no time) — ${d.value}`;
            }
            return `${d.key.toString().padStart(2, '0')}:00 — ${d.value}`;
        });
        hourChart.yAxis().ticks(4);
        hourChart.xAxis().ticks(8);
        this._charts.push(hourChart);
        // Beaufort wind chart --------------------------------------------------------------------
        const beaufortDim = cf.dimension((d) => {
            var _a;
            const b = ((_a = d.beaufort_wind) !== null && _a !== void 0 ? _a : '').trim();
            return b === '' ? '?' : b;
        });
        const beaufortGroup = beaufortDim.group();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const beaufortChart = dc.rowChart(this._beaufortChartEl[0]);
        beaufortChart
            .width(this._approxWidth(this._beaufortChartEl, 320))
            .height(220)
            .margins({ top: 10, right: 10, bottom: 28, left: 10 })
            .dimension(beaufortDim)
            .group(beaufortGroup)
            .elasticX(true)
            .ordering((d) => d.key)
            .label((d) => `Beaufort ${escapeHtml(d.key)} — ${d.value}`)
            .title((d) => `${d.value} sightings`);
        beaufortChart.xAxis().ticks(3);
        this._charts.push(beaufortChart);
        // Wire onFiltered hook (debounced one tick, so multi-chart filterAll batches) ------------
        let pending = null;
        const fire = () => {
            if (pending !== null) {
                clearTimeout(pending);
            }
            pending = setTimeout(() => {
                pending = null;
                if (this._onFiltered && this._cf) {
                    this._onFiltered(this._cf.allFiltered());
                }
            }, 50);
        };
        for (const c of this._charts) {
            c.on('filtered.dashboard', fire);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dc.renderAll();
    }
    /**
     * Reset every chart's filter without rebuilding. Fires onFiltered once at the end.
     */
    resetFilters() {
        for (const c of this._charts) {
            c.filterAll();
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dc.redrawAll();
        if (this._onFiltered && this._cf) {
            this._onFiltered(this._cf.allFiltered());
        }
    }
    /**
     * Drop charts and crossfilter state. Safe to call before setData() or twice in a row.
     */
    clear() {
        for (const c of this._charts) {
            try {
                c.on('filtered.dashboard', null);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                dc.deregisterChart(c);
            }
            catch (_a) {
                // dc throws if already deregistered — ignore
            }
        }
        this._charts = [];
        this._cf = null;
        this._timeChartEl.empty();
        this._speciesChartEl.empty();
        this._hourChartEl.empty();
        this._beaufortChartEl.empty();
        this._statusEl.text('');
        this._resetBtn.prop('disabled', true);
    }
    /**
     * Width helper. The chart slots can be invisible at construction time
     * (Map tab is hidden), in which case .width() returns 0 — fall back to
     * the supplied default so dc draws something on first render.
     */
    _approxWidth(el, fallback) {
        const w = el.width() || 0;
        return w > 0 ? w : fallback;
    }
    /**
     * Resolve a parent (bambooo component, jQuery or selector) to a jQuery
     * handle the widget root can be appended to.
     * @param {ComponentType} element
     * @return {JQuery}
     * @protected
     */
    static _asParent(element) {
        const maybe = element;
        if (maybe && typeof maybe.getElement === 'function') {
            return maybe.getElement();
        }
        return jQuery(element);
    }
}
